/**
 * Quick-Start Guide — In-app onboarding & training for all roles.
 * Sprint 18, Deliverables 2–4
 *
 * Role-aware: shows relevant sections based on the user's role.
 * - Members: PWA install, clock in/out, view history
 * - Officers: + submit for members, view reports, edit/void entries
 * - Admins:  + dashboard, config, audit, roster sync, report generation
 */

import { useState } from 'react';

import { useAuth } from '../hooks/useAuth';
import AppLayout from '../components/layout/AppLayout';

type Section =
  | 'getting-started'
  | 'clock'
  | 'history'
  | 'schedule'
  | 'officer'
  | 'reports'
  | 'admin'
  | 'troubleshooting';

interface GuideSection {
  id: Section;
  title: string;
  minRole: 'member' | 'officer' | 'admin';
}

const SECTIONS: GuideSection[] = [
  { id: 'getting-started', title: 'Getting Started', minRole: 'member' },
  { id: 'clock', title: 'Clocking In & Out', minRole: 'member' },
  { id: 'history', title: 'Viewing Your Hours', minRole: 'member' },
  { id: 'schedule', title: 'Duty Schedule', minRole: 'member' },
  { id: 'officer', title: 'Officer: Submit for Members', minRole: 'officer' },
  { id: 'reports', title: 'Reports', minRole: 'officer' },
  { id: 'admin', title: 'Administration', minRole: 'admin' },
  { id: 'troubleshooting', title: 'Troubleshooting', minRole: 'member' },
];

const ROLE_RANK = { member: 0, officer: 1, admin: 2 };

function hasMinRole(userRole: string, minRole: string): boolean {
  return (ROLE_RANK[userRole as keyof typeof ROLE_RANK] ?? 0) >= (ROLE_RANK[minRole as keyof typeof ROLE_RANK] ?? 0);
}

export default function Guide() {
  const { user } = useAuth();
  const role = user?.role ?? 'member';
  const [activeSection, setActiveSection] = useState<Section>('getting-started');

  const visibleSections = SECTIONS.filter((s) => hasMinRole(role, s.minRole));

  return (
    <AppLayout title="Quick-Start Guide">
      <div className="w-full">
        {/* Subtitle */}
        <p className="text-sm text-content-secondary mt-0 mb-6">
          Welcome to ShiftHappens — your time tracking app for DCVFD.
        </p>

        {/* Section nav */}
        <nav className="flex flex-wrap gap-1.5 mb-6 pb-4 border-b border-edge-muted">
          {visibleSections.map((s) => (
            <button
              key={s.id}
              type="button"
              onClick={() => setActiveSection(s.id)}
              className={`px-3 py-1.5 text-[13px] rounded-md cursor-pointer whitespace-nowrap ${
                activeSection === s.id
                  ? 'bg-action-primary text-content-on-primary border border-transparent'
                  : 'bg-surface-raised text-content-secondary border border-edge-default'
              }`}
            >
              {s.title}
            </button>
          ))}
        </nav>

        {/* Content */}
        <div className="pb-8">
          {activeSection === 'getting-started' && <GettingStartedSection />}
          {activeSection === 'clock' && <ClockSection />}
          {activeSection === 'history' && <HistorySection />}
          {activeSection === 'schedule' && <ScheduleSection />}
          {activeSection === 'officer' && <OfficerSection />}
          {activeSection === 'reports' && <ReportsSection />}
          {activeSection === 'admin' && <AdminSection />}
          {activeSection === 'troubleshooting' && <TroubleshootingSection />}
        </div>
      </div>
    </AppLayout>
  );
}

// ── Section Components ──────────────────────────────────────────────────

function GettingStartedSection() {
  return (
    <div>
      <h2 className="text-xl font-bold text-content-primary mb-4">Getting Started</h2>

      <StepCard
        step={1}
        title="Install the App"
        content={
          <>
            <p className="text-sm text-content-secondary leading-relaxed my-2">
              ShiftHappens works as a Progressive Web App (PWA) — no app store needed.
            </p>
            <h4 className="text-sm font-semibold text-content-primary mt-3 mb-2">Android / Chrome</h4>
            <ol className="text-sm text-content-secondary leading-loose pl-5 my-1">
              <li>
                Open <strong>shifthappens.dcvfd.org</strong> in Chrome
              </li>
              <li>
                Tap the install banner at the bottom, or tap the <strong>three-dot menu</strong> and select{' '}
                <strong>"Install app"</strong>
              </li>
              <li>The app icon will appear on your home screen</li>
            </ol>
            <h4 className="text-sm font-semibold text-content-primary mt-3 mb-2">iPhone / iPad (Safari)</h4>
            <ol className="text-sm text-content-secondary leading-loose pl-5 my-1">
              <li>
                Open <strong>shifthappens.dcvfd.org</strong> in Safari
              </li>
              <li>
                Tap the <strong>Share button</strong> (square with arrow)
              </li>
              <li>
                Scroll down and tap <strong>"Add to Home Screen"</strong>
              </li>
              <li>
                Tap <strong>"Add"</strong> to confirm
              </li>
            </ol>
          </>
        }
      />

      <StepCard
        step={2}
        title="Sign In"
        content={
          <>
            <p className="text-sm text-content-secondary leading-relaxed my-2">
              Tap <strong>"Sign In with DCVFD Account"</strong> on the login screen.
            </p>
            <p className="text-sm text-content-secondary leading-relaxed my-2">
              Use your department Microsoft account (the same email and password you use for department email).
            </p>
            <p className="text-sm text-content-secondary leading-relaxed my-2">
              You'll stay signed in for 24 hours. After that, you'll need to sign in again.
            </p>
          </>
        }
      />

      <StepCard
        step={3}
        title="Allow Location (Optional)"
        content={
          <>
            <p className="text-sm text-content-secondary leading-relaxed my-2">
              When you clock in, the app may ask for location permission. This is <strong>optional</strong> — your punch
              will still go through if you decline.
            </p>
            <p className="text-sm text-content-secondary leading-relaxed my-2">
              Location data helps officers verify station attendance for reporting purposes.
            </p>
          </>
        }
      />
    </div>
  );
}

function ClockSection() {
  return (
    <div>
      <h2 className="text-xl font-bold text-content-primary mb-4">Clocking In & Out</h2>

      <StepCard
        step={1}
        title="Select Your Category"
        content={
          <>
            <p className="text-sm text-content-secondary leading-relaxed my-2">Choose the type of activity:</p>
            <ul className="text-sm text-content-secondary leading-loose pl-5 my-1">
              <li>
                <strong>Operational</strong> — Calls, station duty, standby
              </li>
              <li>
                <strong>Administrative</strong> — Meetings, paperwork, planning
              </li>
              <li>
                <strong>Training</strong> — Drills, classes, certifications
              </li>
              <li>
                <strong>Public Event</strong> — Community events, open house
              </li>
            </ul>
            <p className="text-sm text-content-secondary leading-relaxed my-2">
              If you select <strong>Operational</strong>, you'll also choose your position and apparatus.
            </p>
          </>
        }
      />

      <StepCard
        step={2}
        title="Tap CLOCK IN"
        content={
          <>
            <p className="text-sm text-content-secondary leading-relaxed my-2">
              Tap the large <strong>CLOCK IN</strong> button, then confirm in the dialog.
            </p>
            <p className="text-sm text-content-secondary leading-relaxed my-2">
              The screen will change to show your active shift with a running timer.
            </p>
          </>
        }
      />

      <StepCard
        step={3}
        title="Tap CLOCK OUT"
        content={
          <>
            <p className="text-sm text-content-secondary leading-relaxed my-2">
              When your shift is done, tap the <strong>CLOCK OUT</strong> button.
            </p>
            <p className="text-sm text-content-secondary leading-relaxed my-2">
              Your hours are automatically calculated and recorded. The entry will appear in your Recent list and your
              Hours History.
            </p>
          </>
        }
      />

      <InfoCard
        title="Offline Mode"
        content="If you lose internet connectivity, you can still clock in and out. Your punches are saved locally and automatically synced when you reconnect."
      />
    </div>
  );
}

function HistorySection() {
  return (
    <div>
      <h2 className="text-xl font-bold text-content-primary mb-4">Viewing Your Hours</h2>

      <p className="text-sm text-content-secondary leading-relaxed my-2">
        Access your hours from the <strong>menu (&#9776;)</strong> &rarr; <strong>"My Hours"</strong>, or tap{' '}
        <strong>"View All Hours"</strong> on the home screen summary widget.
      </p>

      <InfoCard
        title="Monthly Summary"
        content="The home screen shows your hours for the current month, broken down by category. This updates automatically after each punch."
      />

      <InfoCard
        title="Full History"
        content="The Hours History page shows all your time entries. You can filter by date range and see details for each entry including category, position, apparatus, and duration."
      />

      <InfoCard
        title="Editing Your Entries"
        content="You can edit your own entries within 24 hours of creation. After 24 hours, contact an officer to make changes. Tap any entry to view details and the edit option."
      />
    </div>
  );
}

function ScheduleSection() {
  return (
    <div>
      <h2 className="text-xl font-bold text-content-primary mb-4">Duty Schedule</h2>

      <p className="text-sm text-content-secondary leading-relaxed my-2">
        Access the schedule from the <strong>menu (&#9776;)</strong> &rarr; <strong>"Duty Schedule"</strong>.
      </p>

      <InfoCard
        title="Today's Duty"
        content="The home screen shows which battalion is on duty today. If it's your battalion, you'll see a highlighted duty indicator."
      />

      <InfoCard
        title="Calendar View"
        content="The schedule page shows a full calendar with battalion duty assignments. You can view by week or month. Holidays are marked with special indicators."
      />

      <InfoCard
        title="5-Battalion Rotation"
        content="DCVFD uses a 5-battalion rotation schedule. The schedule auto-generates monthly. Holidays may modify the rotation — check the calendar for any changes."
      />
    </div>
  );
}

function OfficerSection() {
  return (
    <div>
      <h2 className="text-xl font-bold text-content-primary mb-4">Officer: Submit for Members</h2>

      <StepCard
        step={1}
        title="Open Officer Submit"
        content={
          <p className="text-sm text-content-secondary leading-relaxed my-2">
            From the <strong>menu (&#9776;)</strong>, tap <strong>"Submit for Member"</strong>. This option is only
            visible to officers and admins.
          </p>
        }
      />

      <StepCard
        step={2}
        title="Select Member & Enter Details"
        content={
          <>
            <p className="text-sm text-content-secondary leading-relaxed my-2">
              Choose the member from your battalion roster. Then fill in:
            </p>
            <ul className="text-sm text-content-secondary leading-loose pl-5 my-1">
              <li>Category, position, and apparatus</li>
              <li>Clock-in and clock-out times</li>
              <li>Optional notes</li>
            </ul>
            <p className="text-sm text-content-secondary leading-relaxed my-2">
              <strong>Note:</strong> Officers can only submit entries for members in their own battalion.
            </p>
          </>
        }
      />

      <StepCard
        step={3}
        title="Review & Submit"
        content={
          <p className="text-sm text-content-secondary leading-relaxed my-2">
            Review the entry details and tap <strong>Submit</strong>. The entry is recorded with your name as the
            submitter and appears in the member's history.
          </p>
        }
      />

      <h3 className="text-base font-semibold text-action-primary mt-6 mb-3">Editing & Voiding Entries</h3>

      <InfoCard
        title="Edit Entries"
        content="Officers can edit entries for members in their battalion at any time (no 24-hour limit). Navigate to the member's entry and make changes. All edits are recorded in the audit trail."
      />

      <InfoCard
        title="Void Entries"
        content="To void (soft-delete) an incorrect entry, open the entry details and select Void. The entry remains in the system for audit purposes but is excluded from reports."
      />
    </div>
  );
}

function ReportsSection() {
  return (
    <div>
      <h2 className="text-xl font-bold text-content-primary mb-4">Reports</h2>

      <p className="text-sm text-content-secondary leading-relaxed my-2">
        Access reports from the <strong>menu (&#9776;)</strong> &rarr; <strong>"Reports"</strong>. Available to officers
        and admins.
      </p>

      <InfoCard
        title="Hours Report"
        content="Summary of hours by member for a date range. Filter by battalion, category, and date range. Export to CSV for further analysis."
      />

      <InfoCard
        title="Operational Detail"
        content="Detailed breakdown of operational entries including position, apparatus, and shift type. Useful for LOSAP certification and county reporting."
      />

      <InfoCard
        title="Attendance Report"
        content="Track attendance patterns across members. Shows number of shifts, total hours, and average shift length."
      />

      <InfoCard
        title="LOSAP Report"
        content="Length of Service Awards Program report formatted for Prince William County requirements. Shows qualifying hours by member."
      />

      <InfoCard
        title="Export to CSV"
        content="All reports can be exported to CSV format. Use the Export button on any report view. Files are stored in the system and can be re-downloaded from the admin dashboard."
      />
    </div>
  );
}

function AdminSection() {
  return (
    <div>
      <h2 className="text-xl font-bold text-content-primary mb-4">Administration</h2>

      <p className="text-sm text-content-secondary leading-relaxed my-2">
        Admin tools are accessible from the navigation bar under the Admin section.
      </p>

      <h3 className="text-base font-semibold text-action-primary mt-6 mb-3">On Duty</h3>
      <InfoCard
        title="On-Duty View"
        content="The On Duty page shows all members currently clocked in, with their category, position, apparatus, and elapsed time. This updates every 30 seconds."
      />

      <h3 className="text-base font-semibold text-action-primary mt-6 mb-3">Configuration</h3>
      <InfoCard
        title="Positions & Units"
        content="Manage the list of positions and units from Settings → Positions & Units. You can add, edit, rename, or deactivate items. Deactivated items won't appear in pickers but historical entries are preserved."
      />

      <InfoCard
        title="Holiday Management"
        content="Navigate to Admin > Holidays to add or remove holidays. Holidays affect the battalion rotation schedule — the schedule is automatically adjusted."
      />

      <h3 className="text-base font-semibold text-action-primary mt-6 mb-3">Roster Sync</h3>
      <InfoCard
        title="Airtable Sync"
        content="Member roster syncs automatically from Airtable every day at 3 AM ET. To trigger a manual sync, use the 'Sync Roster from Airtable' button on the admin dashboard. New members are added automatically; deactivated members are flagged."
      />

      <h3 className="text-base font-semibold text-action-primary mt-6 mb-3">Audit Log</h3>
      <InfoCard
        title="Audit Trail"
        content="Navigate to Admin > Audit Log to view all system changes. Filter by member, action type, entity, and date range. Every edit, void, config change, and roster sync is logged with before/after values."
      />

      <h3 className="text-base font-semibold text-action-primary mt-6 mb-3">Report Generation</h3>
      <InfoCard
        title="Generating Reports"
        content="Use the Reports page to generate and export reports. For LOSAP submissions, generate the LOSAP report for the required date range and export to CSV. Verify totals against AgencyWeb during the parallel running period."
      />

      <h3 className="text-base font-semibold text-action-primary mt-6 mb-3">Pilot Management</h3>
      <InfoCard
        title="Launch Checklist"
        content="Navigate to Admin > Launch Readiness to view the pre-launch checklist. This page tracks all deployment prerequisites including data migration status, training completion, and system health."
      />
    </div>
  );
}

function TroubleshootingSection() {
  return (
    <div>
      <h2 className="text-xl font-bold text-content-primary mb-4">Troubleshooting</h2>

      <InfoCard
        title="Can't sign in?"
        content="Make sure you're using your DCVFD Microsoft account. If you've forgotten your password, contact department IT. If you see 'Access Denied', your account may not be in the system yet — contact an admin."
      />

      <InfoCard
        title="Clock in/out not working?"
        content="Check your internet connection. If you're offline, the punch is queued and will sync automatically. If the issue persists after reconnecting, try refreshing the app. If the problem continues, use the Feedback button to report it."
      />

      <InfoCard
        title="Hours look wrong?"
        content="Check that the correct time zone is set on your device. ShiftHappens records times in UTC and converts to Eastern Time for display. If an entry needs correction, edit it within 24 hours or ask an officer."
      />

      <InfoCard
        title="App not updating?"
        content="Close and reopen the app. On iPhone, swipe up from the app switcher. On Android, force-close from recent apps. If the issue persists, clear the browser cache and reload."
      />

      <InfoCard
        title="Need more help?"
        content="Use the Feedback button (available from any page) to report issues during the pilot period. For urgent issues, contact your battalion officer or department IT directly."
      />
    </div>
  );
}

// ── Reusable Card Components ────────────────────────────────────────────

function StepCard({ step, title, content }: { step: number; title: string; content: React.ReactNode }) {
  return (
    <div className="bg-surface-raised border border-edge-muted rounded-lg p-4 mb-4">
      <div className="flex items-center gap-3 mb-3">
        <span className="flex items-center justify-center w-7 h-7 rounded-full bg-action-primary text-content-on-primary text-[13px] font-bold shrink-0">
          {step}
        </span>
        <h3 className="text-base font-semibold text-content-primary m-0">{title}</h3>
      </div>
      <div>{content}</div>
    </div>
  );
}

function InfoCard({ title, content }: { title: string; content: string }) {
  return (
    <div className="bg-surface-sunken border border-edge-muted rounded-lg px-4 py-3.5 mb-3">
      <h4 className="text-sm font-semibold text-action-primary m-0 mb-1.5">{title}</h4>
      <p className="text-[13px] text-content-secondary leading-relaxed m-0">{content}</p>
    </div>
  );
}
